import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Car, Plus, Minus, Download, Search, FileText, CalendarPlus, LogOut, Ban, Trash2, Wallet, SquareParking, Receipt, CalendarRange } from "lucide-react";
import api, { errMsg, downloadBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, StatCard, Money, Loading, EmptyState, Pagination } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { formatTanggal } from "@/lib/format";
import { toast } from "sonner";
import { SlotCountDialog, RentDialog, ExtendDialog, todayLocal, monthStartLocal, daysUntil } from "@/components/parking/ParkingDialogs";
import { ParkingTrend } from "@/components/parking/ParkingTrend";

const STATUS = {
  active: { label: "Aktif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  extended: { label: "Diperpanjang", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ended: { label: "Selesai", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  cancelled: { label: "Dibatalkan", cls: "bg-red-50 text-red-700 border-red-200" },
};
const VEHICLE = { motor: "Motor", mobil: "Mobil", lainnya: "Lainnya" };

function StatusPill({ value }) {
  const s = STATUS[value] || { label: value, cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`} data-testid={`pk-pill-${value}`}>{s.label}</span>;
}

function ExpiryBadge({ endDate }) {
  const left = daysUntil(endDate);
  if (left === null || left > 7) return null;
  const late = left < 0;
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${late ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`} data-testid="pk-expiry-badge">
      {late ? `Lewat ${-left} hari` : left === 0 ? "Berakhir hari ini" : `Berakhir ${left} hari lagi`}
    </span>
  );
}

function SlotCard({ s, canManage, onRent, onExtend, onEnd, onRemove }) {
  const r = s.current_rental;
  const busy = s.status === "terisi";
  return (
    <div className={`relative overflow-hidden rounded-xl border border-border bg-card p-3 transition-shadow hover:shadow-md`} data-testid={`pk-slot-${s.id}`}>
      <div className={`absolute inset-x-0 top-0 h-1 ${busy ? "bg-slate-700" : "bg-emerald-500"}`} />
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 text-base font-bold"><SquareParking className="h-4 w-4 text-primary" />{s.code}</div>
        {canManage && !busy && (
          <button onClick={() => onRemove(s)} className="text-muted-foreground hover:text-destructive" title="Hapus slot" data-testid={`pk-slot-remove-${s.id}`}><Trash2 className="h-4 w-4" /></button>
        )}
      </div>
      <div className={`mt-1 text-xs font-semibold ${busy ? "text-slate-700" : "text-emerald-700"}`}>{busy ? "Terisi" : "Tersedia"}</div>
      {r ? (
        <div className="mt-2 space-y-0.5 text-xs">
          <div className="truncate font-medium text-foreground">{r.renter_name}</div>
          <div className="text-muted-foreground">{VEHICLE[r.vehicle_type] || r.vehicle_type} · {r.plate_number}</div>
          <div className="text-muted-foreground">s/d {formatTanggal(r.end_date)}</div>
          <ExpiryBadge endDate={r.end_date} />
        </div>
      ) : <div className="mt-2 text-xs text-muted-foreground">Belum disewa</div>}
      <div className="mt-3 flex gap-1.5">
        {busy && r ? (
          <>
            <Button size="sm" variant="outline" className="h-8 flex-1 px-2" onClick={() => onExtend({ ...r, slot_code: s.code })} data-testid={`pk-slot-extend-${s.id}`}><CalendarPlus className="mr-1 h-3.5 w-3.5" />Perpanjang</Button>
            <Button size="sm" variant="ghost" className="h-8 px-2 text-emerald-700" title="Selesai" onClick={() => onEnd(r)} data-testid={`pk-slot-end-${s.id}`}><LogOut className="h-4 w-4" /></Button>
          </>
        ) : (
          <Button size="sm" className="h-8 w-full" onClick={() => onRent(s)} data-testid={`pk-slot-rent-${s.id}`}><Car className="mr-1 h-3.5 w-3.5" />Sewakan</Button>
        )}
      </div>
    </div>
  );
}

function ProjectSlots({ project, slots, canManage, onCount, ...handlers }) {
  const busy = slots.filter((s) => s.status === "terisi").length;
  return (
    <SectionCard
      title={`Slot Parkir — ${project.name}`}
      description={`${slots.length} slot · ${busy} terisi · ${slots.length - busy} kosong`}
      testid={`pk-project-${project.id}`}
    >
      {canManage && (
        <div className="mb-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => onCount({ mode: "add", project })} data-testid={`pk-add-${project.id}`}><Plus className="mr-1 h-4 w-4" />Tambah Slot</Button>
          <Button size="sm" variant="outline" onClick={() => onCount({ mode: "reduce", project, freeCount: slots.length - busy })} data-testid={`pk-reduce-${project.id}`}><Minus className="mr-1 h-4 w-4" />Kurangi Slot</Button>
        </div>
      )}
      {slots.length === 0 ? (
        <EmptyState title="Belum ada slot parkir" description="Tambahkan slot untuk mulai menyewakan parkir di proyek ini." icon={SquareParking} />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {slots.map((s) => <SlotCard key={s.id} s={{ ...s, project_name: project.name }} canManage={canManage} {...handlers} />)}
        </div>
      )}
    </SectionCard>
  );
}

function RentalRow({ r, isSuperadmin, onExtend, onEnd, onCancel }) {
  return (
    <tr data-testid={`pk-rental-row-${r.id}`}>
      <td className="py-2.5 pr-3"><div className="font-medium">{r.renter_name}</div><div className="text-xs text-muted-foreground">{r.whatsapp || "-"}</div></td>
      <td className="py-2.5 pr-3"><div className="font-medium">{r.slot_code}</div><div className="text-xs text-muted-foreground">{r.project_name}</div></td>
      <td className="py-2.5 pr-3"><div>{r.plate_number}</div><div className="text-xs text-muted-foreground">{r.vehicle_label}</div></td>
      <td className="py-2.5 pr-3 text-xs">{formatTanggal(r.start_date)} <span className="text-muted-foreground">s/d</span> {formatTanggal(r.end_date)}</td>
      <td className="py-2.5 pr-3">
        {r.months} bulan
        {r.parent_rental_id && <div className="mt-0.5 inline-block rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700" data-testid={`pk-extension-badge-${r.id}`}>Perpanjangan</div>}
      </td>
      <td className="py-2.5 pr-3 font-semibold tabular-nums"><Money value={r.total} /></td>
      <td className="py-2.5 pr-3 text-xs">{r.method_label}</td>
      <td className="py-2.5 pr-3"><StatusPill value={r.status} /></td>
      <td className="py-2.5 pr-3">
        <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" className="h-7 px-2" title="Struk" onClick={() => openReceipt(r.id)} data-testid={`pk-receipt-${r.id}`}><FileText className="h-4 w-4" /></Button>
          {r.status === "active" && <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-700" title="Perpanjang" onClick={() => onExtend(r)} data-testid={`pk-extend-${r.id}`}><CalendarPlus className="h-4 w-4" /></Button>}
          {r.status === "active" && <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700" title="Selesai" onClick={() => onEnd(r)} data-testid={`pk-end-${r.id}`}><LogOut className="h-4 w-4" /></Button>}
          {isSuperadmin && r.status !== "cancelled" && <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" title="Batalkan" onClick={() => onCancel(r)} data-testid={`pk-cancel-${r.id}`}><Ban className="h-4 w-4" /></Button>}
        </div>
      </td>
    </tr>
  );
}

async function openReceipt(id) {
  try {
    const res = await api.get(`/parking/rentals/${id}/receipt`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  } catch (e) { toast.error(errMsg(e)); }
}

function RentalTable({ params, filters, setFilters, setPage, isSuperadmin, handlers }) {
  const { data, isLoading } = useQuery({
    queryKey: ["pk-rentals", params],
    queryFn: async () => (await api.get("/parking/rentals", { params })).data,
  });
  const exportCsv = async () => {
    try {
      const { page, page_size, ...rest } = params;
      const res = await api.get("/parking/rentals/export.csv", { params: rest, responseType: "blob" });
      downloadBlob(res.data, "parkir.csv");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <SectionCard title="Transaksi Sewa Parkir" testid="pk-rentals-section"
      actions={<Button size="sm" variant="outline" onClick={exportCsv} data-testid="pk-export-csv"><Download className="mr-1.5 h-4 w-4" />Ekspor CSV</Button>}>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[180px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cari nama atau plat nomor..." className="h-9 pl-9" value={filters.q} onChange={(e) => { setPage(1); setFilters((f) => ({ ...f, q: e.target.value })); }} data-testid="pk-search" />
        </div>
        <Select value={filters.status} onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, status: v })); }}>
          <SelectTrigger className="h-9 w-[170px]" data-testid="pk-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {isLoading ? <Loading /> : !data?.items?.length ? (
        <EmptyState title="Belum ada transaksi parkir" description="Sewakan slot dari kartu slot di atas." icon={Receipt} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pk-rentals-table">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  {["Penyewa", "Slot", "Kendaraan", "Periode", "Lama", "Total", "Metode", "Status"].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
                  <th className="py-2 pr-3 text-right font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.items.map((r) => <RentalRow key={r.id} r={r} isSuperadmin={isSuperadmin} {...handlers} />)}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} total={data.total} pageSize={data.page_size} onPage={setPage} />
        </>
      )}
    </SectionCard>
  );
}

export default function Parkir() {
  const { isSuperadmin, canManage } = useAuth();
  const { projects, projectId, projectParam } = useProjects();
  const qc = useQueryClient();
  const [from, setFrom] = useState(monthStartLocal());
  const [to, setTo] = useState(todayLocal());
  const [filters, setFilters] = useState({ q: "", status: "all" });
  const [page, setPage] = useState(1);
  const [countDialog, setCountDialog] = useState(null);
  const [rentSlot, setRentSlot] = useState(null);
  const [extendRental, setExtendRental] = useState(null);

  const { data: slots = [], isLoading } = useQuery({
    queryKey: ["pk-slots", projectId],
    queryFn: async () => (await api.get("/parking/slots", { params: projectParam })).data,
  });
  const { data: recap } = useQuery({
    queryKey: ["pk-recap", projectId, from, to],
    queryFn: async () => (await api.get("/parking/recap", { params: { ...projectParam, date_from: from, date_to: to } })).data,
  });
  const params = useMemo(() => ({
    ...projectParam, q: filters.q || undefined, status: filters.status === "all" ? undefined : filters.status, page, page_size: 15,
  }), [projectParam, filters, page]);

  const shownProjects = projectId === "all" ? projects : projects.filter((p) => p.id === projectId);
  const refresh = () => qc.invalidateQueries();
  const post = async (url, msg) => { try { await api.post(url); toast.success(msg); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const handlers = {
    onExtend: setExtendRental,
    onEnd: (r) => { if (window.confirm(`Selesaikan sewa parkir ${r.renter_name}? Slot akan kembali tersedia.`)) post(`/parking/rentals/${r.id}/end`, "Sewa parkir selesai. Slot tersedia kembali."); },
    onCancel: (r) => { if (window.confirm("Batalkan transaksi parkir ini? Pendapatannya tidak dihitung lagi.")) post(`/parking/rentals/${r.id}/cancel`, "Transaksi dibatalkan."); },
  };
  const removeSlot = async (s) => {
    if (!window.confirm(`Hapus slot ${s.code}?`)) return;
    try { await api.delete(`/parking/slots/${s.id}`); toast.success(`Slot ${s.code} dihapus.`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <PageHeader title="Parkir Berbayar" description="Kelola slot parkir tiap proyek dan sewa bulanan. Pendapatan 100% milik pengelola (terpisah dari investor)." />

      <SectionCard title="Pendapatan Parkir" testid="pk-recap-section">
        <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-[150px]" data-testid="pk-recap-from" />
          <span className="text-muted-foreground">s/d</span>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-[150px]" data-testid="pk-recap-to" />
        </div>
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <StatCard testid="pk-income" label="Pendapatan Parkir" value={<Money value={recap?.income} />} icon={Wallet} accent="#047857" hint={`Transfer ${new Intl.NumberFormat("id-ID").format(recap?.by_method?.transfer || 0)} · Tunai ${new Intl.NumberFormat("id-ID").format(recap?.by_method?.cash || 0)}`} />
          <StatCard testid="pk-occupied" label="Slot Terisi" value={`${recap?.slots_occupied || 0} / ${recap?.slots_total || 0}`} icon={SquareParking} accent="#334155" />
          <StatCard testid="pk-count" label="Transaksi" value={recap?.count || 0} icon={Receipt} accent="#dd6030" />
          <StatCard testid="pk-months" label="Bulan Terjual" value={recap?.months_sold || 0} icon={CalendarRange} accent="#0369a1" />
        </div>
      </SectionCard>

      <ParkingTrend projectParam={projectParam} projectId={projectId} />

      {isLoading ? <Loading /> : shownProjects.map((p) => (
        <ProjectSlots key={p.id} project={p} slots={slots.filter((s) => s.project_id === p.id)} canManage={canManage}
          onCount={setCountDialog} onRent={setRentSlot} onRemove={removeSlot} {...handlers} />
      ))}

      <RentalTable params={params} filters={filters} setFilters={setFilters} setPage={setPage} isSuperadmin={isSuperadmin} handlers={handlers} />

      {countDialog && <SlotCountDialog {...countDialog} onClose={() => setCountDialog(null)} onDone={() => { setCountDialog(null); refresh(); }} />}
      {rentSlot && <RentDialog slot={rentSlot} onClose={() => setRentSlot(null)} onDone={() => { setRentSlot(null); refresh(); }} />}
      {extendRental && <ExtendDialog rental={extendRental} onClose={() => setExtendRental(null)} onDone={() => { setExtendRental(null); refresh(); }} />}
    </div>
  );
}
